import * as React from 'react'
import { MutableRefObject, ReactElement, useEffect, useMemo, useState } from 'react'
import BaseList from '../../../components/List/BaseList'
import { DocumentOverviewItemDto, IRootOverviewItemDto } from '../../../config/documentConfig'
import Loading from '../../../components/Loading'
import DocumentListItem from './DocumentListItem'
import { DataPromise, GraphError } from '../../../context/AxiosContext'
import { IList, ISelection, MessageBar, MessageBarType, Stack, Text } from '@fluentui/react'

type Props = {
    componentRef: MutableRefObject<IList>
    selection: ISelection<DocumentOverviewItemDto>
    isLoading: boolean
    rootFolder: DataPromise<IRootOverviewItemDto>
    items: DocumentOverviewItemDto[]
    searchResults: DataPromise<string[], undefined, GraphError>
    reset: () => void // Callback for when the view needs to be updated.
}

export type SearchResults = {
    matchIds: Set<string>
    folderIds: Set<string>
}

export default function DocumentList(props: Props): ReactElement {
    const { rootFolder } = props
    const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set())

    const searchResults = useMemo<SearchResults | undefined>(() => {
        if (!props.isLoading && props.searchResults.loaded && props.items) {
            const results = props.items.filter(item => props.searchResults.data.includes(item.id))
            return {
                matchIds: new Set(results.map(item => item.id)),
                folderIds: new Set(results.flatMap(item => item.stack.map(parent => parent.id))),
            }
        } else {
            return undefined
        }
    }, [props.searchResults.data, props.isLoading])

    const errorMessage = useMemo(
        () => props.searchResults?.error?.response?.data?.error?.message || props.searchResults?.error?.message,
        [props.searchResults],
    )

    useEffect(() => {
        props.componentRef.current?.forceUpdate()
    }, [searchResults])

    if (rootFolder.loading && !rootFolder.loaded) {
        return <Loading label="Bezig met ophalen documentenoverzicht..." />
    }

    if (props.searchResults.loading) {
        return <Loading label="Bezig met zoeken..." />
    }

    return (
        <Stack>
            {searchResults?.matchIds.size === 0 && (
                <MessageBar messageBarType={MessageBarType.error}>
                    De zoekopdracht heeft geen resultaten opgeleverd
                </MessageBar>
            )}
            {props.searchResults.rejected && (
                <MessageBar messageBarType={MessageBarType.error}>
                    <Stack horizontal tokens={{ childrenGap: 's2' }}>
                        <Text variant="small">
                            Er is een fout opgetreden tijdens het uitvoeren van de zoekopdracht:
                        </Text>
                        <Text variant="small">
                            <em>{errorMessage}</em>
                        </Text>
                    </Stack>
                </MessageBar>
            )}
            {props.isLoading && (
                <MessageBar messageBarType={MessageBarType.info}>Bezig met ophalen mappen...</MessageBar>
            )}
            <BaseList<DocumentOverviewItemDto> items={rootFolder.data?.children} componentRef={props.componentRef}>
                {(item: DocumentOverviewItemDto) => (
                    <DocumentListItem
                        key={item.id}
                        item={item}
                        items={props.items}
                        selection={props.selection}
                        searchResults={searchResults}
                        isParentSelected={false}
                        isExpanded={(folderId: string) => expandedFolders.has(folderId)}
                        setExpanded={(folderId: string, value?: boolean) => {
                            const copy = new Set(expandedFolders)
                            copy[(value ?? !expandedFolders.has(folderId)) ? 'add' : 'delete'](folderId)
                            setExpandedFolders(copy)
                        }}
                        reset={props.reset}
                    />
                )}
            </BaseList>
        </Stack>
    )
}
